import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "./api";

const AuthCtx = createContext({
  user: null,
  subscription: null,
  loading: true,
  refresh: () => {},
  refreshSubscription: () => Promise.resolve(null),
  logout: () => {},
  signUp: () => {},
  signIn: () => {},
  signInWithGoogle: () => {},
  resetPassword: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync user & subscription with backend
  const syncUser = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/me");
      const userData = res.data;
      setUser(userData);

      // Process any pending coupon stored before login
      const pendingCoupon = localStorage.getItem("studlyf_pending_coupon");
      if (pendingCoupon) {
        try {
          const actRes = await api.post("/subscription/activate-coupon", { coupon_code: pendingCoupon });
          if (actRes.data.success) {
            toast.success(actRes.data.message || "🎉 STUDLYF Pro activated successfully! Your Pro access is valid for 30 days.");
          }
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (detail === "You have already redeemed this coupon.") {
            toast.error("You have already redeemed this coupon.");
          }
        } finally {
          localStorage.removeItem("studlyf_pending_coupon");
        }
      }

      try {
        const subRes = await api.get("/subscription");
        setSubscription(subRes.data);
      } catch (e) {
        console.error("Error fetching subscription inside syncUser:", e);
      }

      setLoading(false);
      return userData;
    } catch (err) {
      localStorage.removeItem("studlyf_session_token");
      setUser(null);
      setSubscription(null);
      setLoading(false);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    await syncUser();
  }, [syncUser]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signUp = async (email, password, metadata = {}) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/signup", {
        email,
        password,
        name: metadata.name,
        company: metadata.company,
        role: metadata.role,
      });

      if (res.data?.token) {
        localStorage.setItem("studlyf_session_token", res.data.token);
      }

      if (res.data?.user) {
        setUser(res.data.user);
      }

      await syncUser();
      return res.data;
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || "Signup failed.";
      throw new Error(detail);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });

      if (res.data?.token) {
        localStorage.setItem("studlyf_session_token", res.data.token);
      }

      if (res.data?.user) {
        setUser(res.data.user);
      }

      await syncUser();
      return res.data;
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || "Invalid credentials.";
      throw new Error(detail);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    toast.error("Google sign in is available via email/password credentials.");
    throw new Error("Google sign in is available via email/password credentials.");
  };

  const refreshSubscription = useCallback(async () => {
    try {
      const res = await api.get("/subscription");
      setSubscription(res.data);
      return res.data;
    } catch (e) {
      console.error("Error refreshing subscription:", e);
      return null;
    }
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Backend logout failed:", e);
    }
    localStorage.removeItem("studlyf_session_token");
    setUser(null);
    setSubscription(null);
    window.location.href = "/";
  };

  const resetPassword = async (email) => {
    try {
      const res = await api.post("/auth/forgot-password", { email });
      return res.data;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Failed to process reset password.";
      throw new Error(detail);
    }
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        setUser,
        subscription,
        setSubscription,
        refreshSubscription,
        loading,
        refresh,
        logout,
        signUp,
        signIn,
        signInWithGoogle,
        resetPassword,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
